import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy, DollarSign, Share2, ArrowLeft, Check, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CelebrationPage() {
  const { creator, kind, level } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/m/${creator}/${kind}/${level}`)
      .then((r) => setM(r.data))
      .catch(() => setErr(true));
  }, [creator, kind, level]);

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/m/${creator}/${kind}/${level}/share`;
  const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/m/${creator}/${kind}/${level}/og.png`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied", { description: "Paste it on X / LinkedIn / anywhere" });
      setTimeout(() => setCopied(false), 2400);
    } catch {
      toast.error("Couldn't access clipboard — copy manually");
    }
  };

  const shareToX = () => {
    const text = encodeURIComponent(`Just hit ${m?.label} on NEXUS 🎉`);
    window.open(`https://x.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0B1A] text-[#A0A5C0]">
        <div className="text-center" data-testid="celebration-error">
          <div className="text-2xl font-display font-bold text-[#F8FAFF] mb-2">Milestone not found</div>
          <Link to="/" className="text-[#00E5FF] hover:underline">Back to NEXUS</Link>
        </div>
      </div>
    );
  }

  if (!m) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0B1A]">
        <div className="text-[#A0A5C0]" data-testid="celebration-loading">Loading milestone…</div>
      </div>
    );
  }

  const isRevenue = m.kind === "revenue";
  const Icon = isRevenue ? DollarSign : Trophy;
  const accent = isRevenue ? "#00E5FF" : "#F5C542";

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-[#0A0B1A] text-[#F8FAFF]"
      data-testid="celebration-page"
    >
      {/* Halo backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 35%, ${accent}22 0%, transparent 55%)`,
        }}
      />
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <Link
          to={`/u/${m.creator_id}`}
          className="inline-flex items-center gap-2 text-sm text-[#A0A5C0] hover:text-[#F8FAFF] transition"
          data-testid="celebration-back"
        >
          <ArrowLeft size={14} /> Back to {m.creator_name}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-10 text-center"
        >
          <div className="text-xs uppercase tracking-[0.4em] text-[#00E5FF] font-bold">
            NEXUS · MILESTONE
          </div>

          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 16 }}
            className="mt-8 inline-flex items-center justify-center w-32 h-32 rounded-full"
            style={{
              background: `radial-gradient(circle, ${accent}44 0%, transparent 70%)`,
              border: `2px solid ${accent}`,
            }}
            data-testid="celebration-icon"
          >
            <Icon size={52} style={{ color: accent }} />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-8 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight"
            data-testid="celebration-label"
          >
            {m.label}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-4 text-xl text-[#A0A5C0]"
          >
            <span style={{ color: accent }} className="font-bold">{m.creator_name}</span> just crossed it.
          </motion.div>

          {m.reached_at && (
            <div className="mt-2 text-xs text-[#5A6080]" data-testid="celebration-reached-at">
              Reached {new Date(m.reached_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          )}

          {/* OG preview card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.5 }}
            className="mt-12 inline-block rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          >
            <img
              src={ogUrl}
              alt={`${m.label} milestone card`}
              className="block max-w-full w-[640px] h-auto"
              data-testid="celebration-og-preview"
            />
          </motion.div>

          {/* Share buttons */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.45 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              data-testid="celebration-copy-link"
              onClick={copyShare}
              className="inline-flex items-center gap-2 glass px-5 py-3 rounded-full font-semibold text-sm hover:bg-white/10 transition"
            >
              {copied ? <Check size={14} className="text-[#00E5FF]" /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy share link"}
            </button>
            <button
              data-testid="celebration-share-x"
              onClick={shareToX}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm text-[#0A0B1A]"
              style={{
                background: `linear-gradient(90deg, #00E5FF, #7B2CBF)`,
              }}
            >
              <Share2 size={14} /> Share on X
            </button>
            <Link
              to={`/u/${m.creator_id}`}
              data-testid="celebration-view-profile"
              className="inline-flex items-center gap-2 glass px-5 py-3 rounded-full font-semibold text-sm hover:bg-white/10 transition"
            >
              View {m.creator_name}&apos;s profile →
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
